/**
 * 06 거래처 정보 API — Partner Business Relationships.
 * 로그인 업체에 오늘 귀속된 거래 행. 1행 = 담당자 × 병의원 × 제약사
 * (병원급 의약품 단위 거래는 의약품마다 1행). 조회 전용.
 */
import { createApiPath } from '@/shared/api/endpoints';
import { apiClient, toQueryParams } from '@/shared/api/http-client';
import type { PageParams, PageResponse } from '@/shared/api/types';

/** 거래 행 상태 */
export type PartnerTradeStatus = 'TRADING' | 'ENDED';

/**
 * 정렬 기준 (RDL-112). 모두 가나다 오름차순, 이름이 없는 행은 그 키에서 맨 뒤.
 * - USER(기본, 담당자순): 담당자 업체명 → 병의원명 → 제약사명 → 의약품명
 * - CLINIC(병의원명순): 병의원명 → 담당자 업체명 → 제약사명 → 의약품명
 * - REGION(지역순): 시/도 → 시/군/구 → 병의원명 → … (지역 없는 병의원은 맨 뒤)
 * - PHARMA_COMPANY(제약사순): 제약사명 → 의약품명 → 병의원명 → 담당자 업체명
 * 정의되지 않은 값은 400(대소문자 구분).
 */
export const BUSINESS_RELATIONSHIP_SORTS = [
  'USER',
  'CLINIC',
  'REGION',
  'PHARMA_COMPANY',
] as const;

export type BusinessRelationshipSort =
  (typeof BUSINESS_RELATIONSHIP_SORTS)[number];

/** 거래 상태 필터. 서버 기본값 ALL(거래 중 + 거래 종료) */
export type PartnerTradeStatusFilter = PartnerTradeStatus | 'ALL';

export interface BusinessRelationshipListParams extends PageParams {
  /** 병의원명·요양기관번호·의약품명 부분 검색 */
  keyword?: string;
  /**
   * 담당자(다중, 최대 1000) — /partner/filters/users 의 userId. userIds=1&userIds=2 로 보낸다.
   * 같은 필드 안은 OR, 담당자·제약사 사이는 AND. 없으면 전체.
   */
  userIds?: number[];
  /** 제약사(다중, 최대 1000) — /partner/filters/pharma-companies 의 pharmaCompanyId */
  pharmaCompanyIds?: number[];
  /** 없으면 서버 기본값 ALL */
  tradeStatus?: PartnerTradeStatusFilter;
  /** 없으면 서버 기본값 USER */
  sort?: BusinessRelationshipSort;
}

export interface BusinessRelationship {
  /** relationPharmaCompanyId + drugCode 로 행을 식별한다 */
  relationPharmaCompanyId: number;
  /** 제약사 단위(전 품목) 거래면 null */
  drugCode: string | null;
  userId: number;
  userCompanyName: string;
  userRepresentativeName: string;
  clinicId: number;
  clinicName: string | null;
  /** 요양기관번호. 없거나 폐업·삭제로 확인되지 않으면 null */
  ykiho: string | null;
  /** 정식 시/도 + 시/군/구 (예: 경기도 수원시 영통구) */
  region: string | null;
  pharmaCompanyId: number;
  /** 제약사 마스터에 없으면 null */
  pharmaCompanyName: string | null;
  /** 제약사 단위 거래이거나 기준월에 없으면 null */
  drugName: string | null;
  tradeStatus: PartnerTradeStatus;
  /** 서버 표시값 (거래 중 · 거래 종료) */
  tradeStatusView: string;
  /** 제약사 거래 종료일(yyyy-MM-dd). 종료 행에만, 기록 없으면 null */
  endedAt: string | null;
  /** 병의원 폐업일(yyyy-MM-dd). null이면 영업 중. 거래 상태와 무관 */
  clinicClosedOn: string | null;
}

/** 행 key — 같은 제약사 거래에 의약품 행이 여럿일 수 있다 */
export const getBusinessRelationshipRowKey = (row: BusinessRelationship) =>
  `${row.relationPharmaCompanyId}:${row.drugCode ?? '*'}`;

export const businessRelationshipService = {
  /** GET /partner/business-relationships */
  list: async (params: BusinessRelationshipListParams = {}) => {
    const { data } = await apiClient.get<PageResponse<BusinessRelationship>>(
      createApiPath.businessRelationships.list(),
      { params: toQueryParams(params) },
    );
    return data;
  },
};
